import { type ButtonHTMLAttributes, type ReactNode } from "react";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "accent" | "secondary" | "outline" | "ghost" | "danger";
  size?: "sm" | "md" | "lg";
  loading?: boolean;
  icon?: ReactNode;
  fullWidth?: boolean;
  children: ReactNode;
}

export default function Button({
  variant = "primary",
  size = "md",
  loading = false,
  icon,
  fullWidth = false,
  children,
  className = "",
  disabled,
  ...props
}: ButtonProps) {
  const base =
    "inline-flex items-center justify-center gap-2 font-medium rounded-lg transition-all duration-150 whitespace-nowrap cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed";

  const variants: Record<string, string> = {
    primary:
      "bg-primary-500 text-white hover:bg-primary-600 focus:ring-primary-400 active:bg-primary-700",
    accent:
      "bg-accent-500 text-white hover:bg-accent-600 focus:ring-accent-400 active:bg-accent-700",
    secondary:
      "bg-secondary-500 text-white hover:bg-secondary-600 focus:ring-secondary-400 active:bg-secondary-700",
    outline:
      "border-2 border-background-300 text-foreground-700 hover:bg-background-100 focus:ring-primary-400 active:bg-background-200",
    ghost:
      "text-foreground-600 hover:bg-background-100 hover:text-foreground-900 focus:ring-primary-400",
    danger:
      "bg-red-600 text-white hover:bg-red-700 focus:ring-red-400 active:bg-red-800",
  };

  const sizes: Record<string, string> = {
    sm: "text-xs px-3 py-1.5 rounded-md",
    md: "text-sm px-4 py-2",
    lg: "text-base px-6 py-2.5",
  };

  return (
    <button
      className={`${base} ${variants[variant]} ${sizes[size]} ${
        fullWidth ? "w-full" : ""
      } ${className}`}
      disabled={disabled || loading}
      {...props}
    >
      {loading ? (
        <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
      ) : icon ? (
        <span className="flex items-center justify-center w-4 h-4">{icon}</span>
      ) : null}
      {children}
    </button>
  );
}